/**
 * CustomizationPanel - Expanded customization options for a menu item
 * Contains size, modifiers, modifier groups, ingredients, add-ons, notes, quantity, and add-to-cart
 */
import { motion } from 'framer-motion';
import { Heart, X } from 'lucide-react';
import { Button } from '../ui/button';
import {
  SizeSelector,
  ModifierSelector,
  ModifierGroupSelector,
  IngredientList,
  AddOnSelector,
  QuantitySelector,
  PopularCustomizations,
  SpecialNotes
} from './index';

const popAnimation = {
  initial: { scale: 1 },
  pop: { 
    scale: [1, 1.2, 0.9, 1.1, 1],
    transition: { duration: 0.4, ease: "easeInOut" }
  }
};

export default function CustomizationPanel({
  // Data
  loadingCustomization,
  popularCustomizations,
  sizes,
  modifiers,
  modifierGroups,
  ingredients,
  compatibleAddOns,
  // State
  selectedSize,
  selectedModifiers,
  removedIngredients,
  extraIngredients,
  selectedAddOns,
  specialNotes,
  quantity,
  finalPrice,
  isAddingToCart,
  isAuthenticated,
  isFavorite,
  itemId,
  // Callbacks
  onApplyCustomization,
  onSelectSize,
  onToggleModifier,
  onSelectModifierGroupOption,
  onDeselectModifierGroupOption,
  onToggleRemove,
  onToggleExtra,
  onToggleAddOn,
  onIncreaseAddOnQuantity,
  onDecreaseAddOnQuantity,
  setSpecialNotes,
  setQuantity,
  handleAddToCart,
  handleSaveFavorite,
  resetAndClose,
  // Helpers
  getModifierPrice,
  isCoreIngredient,
  getIngredientStepNumber,
  addToCartButtonRef
}) {
  if (loadingCustomization) {
    return (
      <div className="p-4 space-y-5 bg-muted/30">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-5 bg-muted/30">
      <PopularCustomizations 
        popularCustomizations={popularCustomizations}
        onApplyCustomization={onApplyCustomization}
      />

      <SizeSelector 
        sizes={sizes}
        selectedSize={selectedSize}
        onSelectSize={onSelectSize}
        stepNumber={1}
      />

      <ModifierSelector 
        modifiers={modifiers}
        selectedModifiers={selectedModifiers}
        onToggleModifier={onToggleModifier}
        getModifierPrice={getModifierPrice}
        stepNumber={sizes.length > 0 ? 2 : 1}
      />

      <ModifierGroupSelector 
        modifierGroups={modifierGroups}
        selectedModifiers={selectedModifiers}
        onSelectOption={onSelectModifierGroupOption}
        onDeselectOption={onDeselectModifierGroupOption}
        stepNumber={sizes.length > 0 && modifiers.length > 0 ? 3 : sizes.length > 0 || modifiers.length > 0 ? 2 : 1}
      />

      <IngredientList 
        ingredients={ingredients}
        removedIngredients={removedIngredients}
        extraIngredients={extraIngredients}
        onToggleRemove={onToggleRemove}
        onToggleExtra={onToggleExtra}
        isCoreIngredient={isCoreIngredient}
        stepNumber={getIngredientStepNumber()}
      />

      <AddOnSelector 
        addOns={compatibleAddOns}
        selectedAddOns={selectedAddOns}
        onToggleAddOn={onToggleAddOn}
        onIncreaseQuantity={onIncreaseAddOnQuantity}
        onDecreaseQuantity={onDecreaseAddOnQuantity}
      />

      <SpecialNotes 
        value={specialNotes}
        onChange={setSpecialNotes}
      />

      {/* Quantity & Add to Cart */}
      <div className="pt-3 border-t border-border" ref={addToCartButtonRef}>
        <div className="flex items-center justify-between mb-3">
          <QuantitySelector 
            quantity={quantity}
            onIncrease={() => setQuantity(q => q + 1)}
            onDecrease={() => setQuantity(q => Math.max(1, q - 1))}
          />
          <span className="font-heading text-xl text-primary">
            {(finalPrice * quantity).toFixed(2)}
          </span>
        </div>
        
        <div className="flex gap-2">
          <motion.div 
            className="flex-1"
            animate={isAddingToCart ? popAnimation.pop : popAnimation.initial}
          >
            <Button
              onClick={handleAddToCart}
              className="w-full rounded-full bg-primary hover:bg-primary/90 h-12 text-base"
              data-testid="confirm-add-to-cart"
            >
              Add {quantity} to Order • ${(finalPrice * quantity).toFixed(2)}
            </Button>
          </motion.div>
          
          {isAuthenticated && (
            <Button
              onClick={handleSaveFavorite}
              variant="outline"
              className={`rounded-full border-2 h-12 px-4 ${
                isFavorite(itemId, selectedSize)
                  ? 'border-accent text-accent'
                  : 'border-primary/20 hover:border-primary'
              }`}
              data-testid="save-to-favorites"
            >
              <Heart className={`w-5 h-5 ${isFavorite(itemId, selectedSize) ? 'fill-accent' : ''}`} />
            </Button>
          )}
          
          <Button
            onClick={resetAndClose}
            variant="ghost"
            className="rounded-full h-12 px-4"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>
      </div>
    </div>
  );
}
